import type { FirebaseApp } from 'firebase/app';
import { connectFunctionsEmulator, getFunctions, httpsCallable, type Functions } from 'firebase/functions';

import type { EmulatorEndpoint } from '@taki/runtime-config';
import { AUTEUR_SYSTEME, EVENEMENT_FICHE_MODIFIEE } from '@taki/data-model';

/** La Function qui dit si la base d'une fiche a son chatter allume (`functions/src/chatter/gardeChatter.ts`). */
export const CALLABLE_ETAT_DU_CHATTER = 'etatDuChatter';
export const CHATTER_FUNCTIONS_REGION = 'europe-west1';

/** Lit l'etat du chatter d'une fiche : `true` = allume. */
export type LireEtatDuChatter = (ancre: { family: string; recordId: string }) => Promise<boolean>;

const emulatorBoundFunctions = new WeakSet<Functions>();

/**
 * L'ETAT DU CHATTER, LU AU SERVEUR (verification independante du 06/10,
 * ecart C) : la meme lecture que la garde `gardeChatterDeFiche`, pour que
 * l'ecran et le serveur ne divergent jamais. Le client ne connait pas les
 * declarations de toutes les apps ; la Function, si.
 */
export function createLecteurEtatDuChatter(app: FirebaseApp, emulator?: EmulatorEndpoint | undefined): LireEtatDuChatter {
  let appeler: ReturnType<typeof httpsCallable<unknown, { actif?: unknown }>> | null = null;
  const lire: LireEtatDuChatter = async (ancre) => {
    if (appeler === null) {
      const functions = getFunctions(app, CHATTER_FUNCTIONS_REGION);
      if (emulator && !emulatorBoundFunctions.has(functions)) {
        connectFunctionsEmulator(functions, emulator.host, emulator.port);
        emulatorBoundFunctions.add(functions);
      }
      appeler = httpsCallable<unknown, { actif?: unknown }>(functions, CALLABLE_ETAT_DU_CHATTER);
    }
    const reponse = await appeler({ family: ancre.family, recordId: ancre.recordId });
    return reponse.data?.actif !== false;
  };
  return memoriserLesAllumes(lire, emulator === undefined ? DUREE_DES_ALLUMES_MS : DUREE_DES_ALLUMES_EMULATEUR_MS);
}

/**
 * LA MEMOIRE DES SUIVIS ALLUMES (chantier `suivi-volet`, 29/10/2026) : chaque
 * envoi dans un suivi demandait l'etat du chatter a la Function, un aller et
 * retour de plus avant la confirmation du message, a chaque message. Un
 * `true` se garde aussi longtemps que le serveur garde le reglage
 * (`DUREE_DU_CACHE_MS` de `functions/src/chatter/reglage.ts` : une minute,
 * deux secondes sous emulateur) : le client n'est jamais plus en retard que
 * la garde du serveur. Un `false` et un echec ne se gardent pas : une base
 * eteinte se redemande, un rallumage se voit tout de suite.
 */
export const DUREE_DES_ALLUMES_MS = 60_000;
export const DUREE_DES_ALLUMES_EMULATEUR_MS = 2_000;

export function memoriserLesAllumes(lire: LireEtatDuChatter, dureeMs: number, maintenant: () => number = Date.now): LireEtatDuChatter {
  const allumes = new Map<string, number>();
  return async (ancre) => {
    const cle = `${ancre.family}/${ancre.recordId}`;
    const vu = allumes.get(cle);
    if (vu !== undefined && maintenant() - vu < dureeMs) return true;
    allumes.delete(cle);
    const actif = await lire(ancre);
    if (actif) allumes.set(cle, maintenant());
    return actif;
  };
}

/* -------------------------------------------------------------------------- */
/* Les comptes du suivi d'une fiche (chantier `fiche-suivi`)                   */
/* -------------------------------------------------------------------------- */

/** Le total du fil d'un suivi et sa decomposition, comptes au serveur (jamais sur la page chargee). */
export interface CompteDuSuivi {
  total: number;
  messages: number;
  changements: number;
  evenements: number;
}

/**
 * LES EGALITES QUI DECOMPOSENT LE FIL, en plus du cadrage d'une liste de
 * messages (`type: 'conversation'`, `conversationId`, non archives) : les
 * evenements systeme par leur auteur, les changements par leur evenement.
 * Les messages et les evenements autres se deduisent par difference. Des
 * egalites SEULES : Firestore les sert par ses index a champ unique, aucun
 * index composite (`describeAggregateIndex` rend `null`, `pnpm index:check`).
 * Le partage est celui de `sorteDuMessageDeSuivi` (@taki/data-model).
 */
export const FILTRES_DU_SUIVI = {
  systeme: { auteur: AUTEUR_SYSTEME },
  changements: { auteur: AUTEUR_SYSTEME, 'systeme.evenement': EVENEMENT_FICHE_MODIFIEE },
} as const;

/**
 * Trois `count()` serveur, en parallele : le total, les evenements systeme,
 * les changements. `compter` recoit les egalites de plus (`undefined` : le
 * total). Exporte pour le test : la difference ne descend jamais sous zero
 * (deux comptes lus a des instants voisins peuvent se croiser d'une ecriture).
 */
export async function compterLeSuiviPar(
  compter: (where?: Readonly<Record<string, unknown>>) => Promise<number>,
): Promise<CompteDuSuivi> {
  const [total, systemes, changements] = await Promise.all([
    compter(),
    compter(FILTRES_DU_SUIVI.systeme),
    compter(FILTRES_DU_SUIVI.changements),
  ]);
  return {
    total,
    messages: Math.max(0, total - systemes),
    changements,
    evenements: Math.max(0, systemes - changements),
  };
}
